import { webConfig } from '../config';
import { codeChallengeS256, randomToken } from './pkce';

export interface TokenSet {
  accessToken: string;
  refreshToken: string | null;
  expiresAt: number; // epoch ms
}

interface AsMetadata {
  authorization_endpoint: string;
  token_endpoint: string;
}

let metaPromise: Promise<AsMetadata> | null = null;

// RFC 8414 discovery — the AS is the gate for endpoint URLs so we never hardcode
// /authorize vs /token wrong.
async function discover(): Promise<AsMetadata> {
  if (!metaPromise) {
    metaPromise = fetch(`${webConfig.issuer}/.well-known/oauth-authorization-server`).then(
      async (r) => {
        if (!r.ok) throw new Error(`AS discovery failed: ${r.status}`);
        const j = (await r.json()) as AsMetadata;
        return {
          authorization_endpoint: j.authorization_endpoint,
          token_endpoint: j.token_endpoint,
        };
      },
    );
  }
  return metaPromise;
}

const VERIFIER_KEY = 'almanac.pkce_verifier';
const STATE_KEY = 'almanac.oauth_state';

function toTokenSet(json: {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
}): TokenSet {
  const expiresIn = typeof json.expires_in === 'number' ? json.expires_in : 3600;
  return {
    accessToken: json.access_token,
    refreshToken: json.refresh_token ?? null,
    expiresAt: Date.now() + expiresIn * 1000,
  };
}

export async function beginLogin(): Promise<void> {
  const verifier = randomToken();
  const state = randomToken(16);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  const challenge = await codeChallengeS256(verifier);
  const meta = await discover();

  const url = new URL(meta.authorization_endpoint);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', webConfig.clientId);
  url.searchParams.set('redirect_uri', webConfig.redirectUri);
  url.searchParams.set('scope', webConfig.scope);
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  // Force a consent prompt. oidc-provider strips `offline_access` from the
  // request unless `prompt=consent` is present, and without it in the grant no
  // refresh token is issued — so login can't survive past the 1h access-token
  // TTL. This keeps `offline_access` and gets us a refresh token.
  url.searchParams.set('prompt', 'consent');
  // Ask the AS to mint a token whose `aud` is our resource server (§5.6).
  url.searchParams.set('resource', webConfig.audience);
  window.location.assign(url.toString());
}

export async function completeLogin(params: URLSearchParams): Promise<TokenSet> {
  const error = params.get('error');
  if (error)
    throw new Error(`authorization error: ${error} ${params.get('error_description') ?? ''}`);

  const code = params.get('code');
  const state = params.get('state');
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);

  if (!code || !state || !verifier) throw new Error('missing code / state / verifier');
  if (state !== expectedState) throw new Error('state mismatch');

  const meta = await discover();
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: webConfig.redirectUri,
    client_id: webConfig.clientId,
    code_verifier: verifier,
    resource: webConfig.audience,
  });
  const res = await fetch(meta.token_endpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!res.ok) throw new Error(`token exchange failed: ${res.status}`);
  return toTokenSet(await res.json());
}

export async function refreshTokens(refreshToken: string): Promise<TokenSet> {
  const meta = await discover();
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
    client_id: webConfig.clientId,
    resource: webConfig.audience,
  });
  const res = await fetch(meta.token_endpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!res.ok) throw new Error(`refresh failed: ${res.status}`);
  return toTokenSet(await res.json());
}
